import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import "./Address.css";
import "./utilities.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL;
const API = `${API_BASE_URL}/api/user` ;

function Address() {
  const token = localStorage.getItem("token");

  const emptyForm = useMemo(
    () => ({
      fullName: "",
      phone: "",
      address: "",
      locality: "",
      city: "",
      state: "",
      pincode: "",
      landmark: "",
      addressType: "Home",
      isDefault: false,
    }),
    []
  );

  const [addresses, setAddresses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (token) {
      loadAddresses();
    }
  }, [token]);

  useEffect(() => {
    if (!showModal) return;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeModal();
      }
    };

    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [showModal]);

  const loadAddresses = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setAddresses(res.data.addresses || []);
    } catch (err) {
      console.log(err);
      setError("Unable to load addresses.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;

    setError("");
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validateForm = () => {
    if (!form.fullName.trim()) return "Full name is required";
    if (!/^[6-9]\d{9}$/.test(form.phone)) return "Enter a valid 10-digit phone number";
    if (!form.address.trim()) return "Address is required";
    if (!form.locality.trim()) return "Locality is required";
    if (!form.city.trim()) return "City is required";
    if (!form.state.trim()) return "State is required";
    if (!/^\d{6}$/.test(form.pincode)) return "Enter a valid 6-digit pincode";
    return "";
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm(emptyForm);
    setError("");
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError("");
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditingId(item._id);
    setForm({
      fullName: item.fullName || "",
      phone: item.phone || "",
      address: item.address || "",
      locality: item.locality || "",
      city: item.city || "",
      state: item.state || "",
      pincode: item.pincode || "",
      landmark: item.landmark || "",
      addressType: item.addressType || "Home",
      isDefault: !!item.isDefault,
    });
    setError("");
    setShowModal(true);
  };

  const saveAddress = async () => {
    const validationError = validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      setError("");

      if (editingId) {
        await axios.put(`${API}/address/${editingId}`, form, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } else {
        await axios.post(`${API}/address`, form, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }

      closeModal();
      loadAddresses();
    } catch (err) {
      console.log(err);
      setError(err.response?.data?.message || "Unable to save address");
    } finally {
      setSaving(false);
    }
  };

  const deleteAddress = async (id) => {
    if (!window.confirm("Delete this address?")) return;

    try {
      await axios.delete(`${API}/address/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      loadAddresses();
    } catch (err) {
      console.log(err);
      alert(err.response?.data?.message || "Unable to delete address");
    }
  };

  const modalMarkup = showModal
    ? createPortal(
        <div className="modal" onClick={closeModal}>
          <div
            className="modal-content address-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="address-modal-title"
          >
            <h2 id="address-modal-title">
              {editingId ? "Edit Address" : "Add Address"}
            </h2>

            <div className="modal-form-grid">
              <input
                name="fullName"
                placeholder="Full Name"
                value={form.fullName}
                onChange={handleChange}
              />

              <input
                name="phone"
                placeholder="Phone Number"
                value={form.phone}
                onChange={handleChange}
              />

              <textarea
                name="address"
                placeholder="House No., Street..."
                value={form.address}
                onChange={handleChange}
                className="full-width"
              />

              <input
                name="locality"
                placeholder="Locality"
                value={form.locality}
                onChange={handleChange}
              />

              <input
                name="city"
                placeholder="City"
                value={form.city}
                onChange={handleChange}
              />

              <input
                name="state"
                placeholder="State"
                value={form.state}
                onChange={handleChange}
              />

              <input
                name="pincode"
                placeholder="Pincode"
                value={form.pincode}
                onChange={handleChange}
              />

              <input
                name="landmark"
                placeholder="Landmark"
                value={form.landmark}
                onChange={handleChange}
              />

              <select
                name="addressType"
                value={form.addressType}
                onChange={handleChange}
              >
                <option>Home</option>
                <option>Office</option>
                <option>Other</option>
              </select>

              <label className="checkbox-group full-width">
                <input
                  type="checkbox"
                  name="isDefault"
                  checked={form.isDefault}
                  onChange={handleChange}
                />
                Set as Default Address
              </label>
            </div>

            {error && (
              <p
                style={{
                  color: "#dc3545",
                  marginTop: "14px",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                {error}
              </p>
            )}

            <div className="modal-buttons">
              <button
                className="theme-btn"
                onClick={saveAddress}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Address"}
              </button>

              <button className="outline-btn" onClick={closeModal} disabled={saving}>
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <div className="profile-card">
        <div className="card-title">
          <h2>Saved Addresses</h2>
          <button className="theme-btn" onClick={openAdd}>
            + Add Address
          </button>
        </div>

        <div className="address-grid">
          {!loading && addresses.length === 0 && (
            <div className="empty-address">
              <h3>No Address Added</h3>
              <p>Add your delivery address.</p>
            </div>
          )}

          {loading && (
            <div className="empty-address">
              <h3>Loading...</h3>
              <p>Please wait while we fetch your saved addresses.</p>
            </div>
          )}

          {!loading &&
            addresses.map((item) => (
              <div className="address-card" key={item._id}>
                {item.isDefault && (
                  <span className="default-badge">Default</span>
                )}

                <h3>{item.fullName}</h3>
                <p>{item.address}</p>
                <p>{item.locality}</p>
                <p>
                  {item.city}, {item.state}
                </p>
                <p>{item.pincode}</p>
                <p className="phone">{item.phone}</p>

                <span className="address-type">
                  {item.addressType || "Home"}
                </span>

                <div className="address-actions">
                  <button className="edit-btn" onClick={() => openEdit(item)}>
                    Edit
                  </button>

                  <button
                    className="delete-btn"
                    onClick={() => deleteAddress(item._id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
        </div>
      </div>

      {modalMarkup}
    </>
  );
}

export default Address;